'use client';
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';

interface AuthContextType {
  token: string | null;
  login: (token: string) => void;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [token, setToken] = useState<string | null>(null);
  const router = useRouter();
  const authApi = process.env.NEXT_PUBLIC_AUTH_API || 'http://localhost:8080';

  const isTokenExpired = (t: string) => {
    try {
      const payload = JSON.parse(atob(t.split('.')[1]));
      return payload.exp * 1000 < Date.now();
    } catch (e) {
      return true;
    }
  };

  const refreshAccessToken = async () => {
    try {
      const res = await fetch(`${authApi}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const newToken = await res.text();
        setToken(newToken);
        localStorage.setItem('organic_heaven_token', newToken);
        return newToken;
      }
    } catch (err) {
      console.error('Failed to refresh token', err);
    }
    return null;
  };

  useEffect(() => {
    const savedToken = localStorage.getItem('organic_heaven_token');
    
    const initializeAuth = async () => {
      if (savedToken) {
        setToken(savedToken);
        refreshAccessToken();
        return;
      }
      await refreshAccessToken();
    };

    initializeAuth();
  }, []);

  useEffect(() => {
    if (!token) return;

    const interval = setInterval(async () => {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const timeRemaining = payload.exp * 1000 - Date.now();
        if (timeRemaining < 5 * 60 * 1000) {
          await refreshAccessToken();
        }
      } catch (e) {
        // ignore
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [token]);

  const login = (newToken: string) => {
    setToken(newToken);
    localStorage.setItem('organic_heaven_token', newToken);
    router.push('/');
  };

  const logout = async () => {
    try {
      await fetch(`${authApi}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (e) {
      console.error('Failed to logout backend session', e);
    }
    setToken(null);
    localStorage.removeItem('organic_heaven_token');
    router.push('/login');
  };

  return (
    <AuthContext.Provider value={{ token, login, logout, isAuthenticated: !!token }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
